import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  DEFAULT_CURRENCY_CODE,
  Injector,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import {
  RxMemoryStorage,
  RxSessionStorage,
  RxLocalStorage,
} from '@rxjs-storage/core';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { LoadingPanelModule } from './shared/components/loading-panel/loading-panel.module';
import { ApiInterceptor } from './shared/interceptors/api.interceptor';
import { AppConfigService } from './shared/services/app-config.service';
import { AuthService, AUTH_STORAGE } from './shared/services/auth.service';
import { GeolocationService } from './shared/services/geolocation.service';
import { LoadingController } from './shared/helpers/loading.controller';
import { ToastService } from './shared/services/toast.service';
import { ReadLayersService } from './shared/services/read-layers.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ExportService } from './shared/services/export.service';
import { ArcGisJobService } from './shared/services/arcgis-job.service';
import { AddressService } from './shared/services/address.service';
import { LoadProcessService } from './shared/services/load-process.service';
import { MapDomainService } from './shared/services/map-domain.service';
import { CacheFactoryService } from './shared/services/cache-factory.service';
import { EntityService } from './shared/services/entity.service';
import { PortugalWkidService } from './shared/services/portugal-wkid.service';
import { MapsService } from './shared/services/maps.service';
import { PrintMapService } from './shared/services/print-map.service';
import { NotesSymbolService } from './shared/services/notes.symbol.service';
import { UploadService } from './shared/services/upload.service';
import { OccurrenceService } from './shared/services/occurrence.service';
import { AnnouncementService } from './shared/services/announcement.service';
import { AppTrackingService } from './shared/services/app-tracking.service';
import { MapSymbolService } from './shared/services/map-symbol.service';
import { RxArcgisPortalAuthService } from '@rxjs-arcgis-auth/core';
import { EventBusService } from './shared/services/event-bus.service';
import { EventsService } from './shared/services/events.service';
import { ScreenService } from './shared/services/screen.service';
import { SiiaAttachmentsService } from './shared/services/siia-attachments.service';
import { SideMenuService } from './shared/services/side-menu.service';
import { APP_STORAGE } from './shared/constants/app.constant';
import esriConfig from '@arcgis/core/config';
import { TRANSLATION_STORAGE } from '@services/translation.service';
import { availableLanguages, defaultLanguage } from 'translations';

let appConfig: AppConfigService;

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    AppRoutingModule,
    LoadingPanelModule,
  ],
  providers: [
    {
      provide: AppConfigService,
      useFactory: () => {
        return appConfig;
      },
    },
    {
      provide: RxArcgisPortalAuthService,
      useFactory: (appConfig: AppConfigService) => {
        return new RxArcgisPortalAuthService({
          portalUrl: appConfig.portalUrl,
          portalAppId: appConfig.portalAppId,
        });
      },
      deps: [AppConfigService],
    },
    {
      provide: AUTH_STORAGE,
      useFactory: (appConfig: AppConfigService) => {
        if (appConfig.internalMode) {
          return new RxMemoryStorage(appConfig.appName);
        }

        return new RxSessionStorage(appConfig.appName);
      },
      deps: [AppConfigService],
    },
    {
      provide: TRANSLATION_STORAGE,
      useFactory: (appConfig: AppConfigService) => {
        return new RxLocalStorage(appConfig.appName);
      },
      deps: [AppConfigService],
    },
    {
      provide: APP_STORAGE,
      useFactory: (appConfig: AppConfigService) => {
        return new RxLocalStorage(appConfig.appName);
      },
      deps: [AppConfigService],
    },
    AuthService,
    SideMenuService,
    MapsService,
    MapDomainService,
    MapSymbolService,
    EntityService,
    AnnouncementService,
    OccurrenceService,
    EventsService,
    GeolocationService,
    ReadLayersService,
    UploadService,
    LoadProcessService,
    SiiaAttachmentsService,
    ExportService,
    ArcGisJobService,
    AddressService,
    PrintMapService,
    PortugalWkidService,
    NotesSymbolService,
    AppTrackingService,
    EventBusService,
    ToastService,
    CacheFactoryService,
    ScreenService,
    {
      provide: LoadingController,
      useFactory: () => new LoadingController(),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (injector: Injector) => {
        return async () => {
          let savedLang =
            localStorage.getItem('GEO.ANACOM.translation:info') ??
            defaultLanguage.code;

          savedLang = savedLang.replaceAll('"', '');

          let configName = 'config.json';

          if (savedLang === availableLanguages.en) {
            configName = 'config.en.json';
          }

          const config = await fetch(configName).then((x) => x.json());
          appConfig = new AppConfigService(config);

          const title = injector.get(Title);
          title.setTitle(appConfig.appName);

          esriConfig.portalUrl = appConfig.portalUrl;
          esriConfig.request.proxyUrl = appConfig.normalizeUrl('~Proxy');

          esriConfig.request.interceptors.push({
            before: (params) => {
              if (params.requestOptions?.withCredentials) {
                params.requestOptions.withCredentials = false;
              }
            },
          });

          const unathorizedMsg = 'Não foi possível a autenticação!';

          if (appConfig.siiaMode) {
            const url = new URL(location.href);
            const searchTokenParam = appConfig.searchTokenParam ?? 'token';
            if (url.searchParams.has(searchTokenParam)) {
              const token = url.searchParams.get(searchTokenParam);
              url.searchParams.delete(searchTokenParam);
              const href = url.toString();

              await new Promise(() => {
                injector
                  .get(AuthService)
                  .logInByToken(token)
                  .subscribe({
                    next: () => {
                      location.href = href;
                    },
                    error: (response) => {
                      if (!response) {
                        return;
                      }

                      injector.get(ToastService).error(unathorizedMsg);
                    },
                  });
              });
            }
          } else if (appConfig.internalMode) {
            await injector
              .get(AuthService)
              .requireEsriAuthInfoAsync()
              .catch(() => {
                injector.get(ToastService).error(unathorizedMsg);
              });
          }

          injector.get(AppTrackingService).sendAppAccess();
        };
      },
      multi: true,
      deps: [Injector],
    },
    {
      provide: LOCALE_ID,
      useValue: 'pt-PT',
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'BRL',
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
